import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import FilterListIcon from '@mui/icons-material/FilterList'
import { tokens } from '../../theme.js'
import { SecondaryButton, Popover, FieldLabel, FilterChip, SearchInput, LinkButton, MutedText, plural } from '../styles/index.js'
import { STATUS_LABEL, FILTER_STATUSES } from './fileStatus.js'
import { EMPTY_FILTER, kindsIn, filterCount } from './tableView.js'

const colors = tokens.colors
const DEBOUNCE_MS = 250

const Panel = styled.div`
  width: 360px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`
const RAIL = { height: 28, padding: '0 14px' }
const ENGAGED = { background: colors.primaryLight, borderColor: colors.primary, color: colors.text.body }
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
`
const Result = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid ${colors.background.medium};
  padding-top: 12px;
`

function ChipGroup({ label, values, on, labelOf, testId, onToggle }) {
  if (!values.length) return null
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <Chips>
        {values.map(v => (
          <FilterChip key={v} type="button" aria-pressed={on.includes(v)} onClick={() => onToggle(v)} data-testid={`${testId}-${v}`}>
            {labelOf ? labelOf(v) : v}
          </FilterChip>
        ))}
      </Chips>
    </div>
  )
}

export function FilterControl({ files, filter, onChange, shownCount }) {
  const [anchor, setAnchor] = useState(null)
  const [typed, setTyped] = useState(filter.name)
  const count = filterCount(filter)
  useEffect(() => {
    setTyped(filter.name)
  }, [filter.name])
  useEffect(() => {
    if (typed === filter.name) return undefined
    const t = setTimeout(() => onChange({ ...filter, name: typed }), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [typed])

  const toggle = section => v =>
    onChange({
      ...filter,
      [section]: filter[section].includes(v) ? filter[section].filter(x => x !== v) : [...filter[section], v]
    })
  const engaged = count > 0 || !!anchor
  return (
    <>
      <SecondaryButton
        type="button"
        onClick={e => setAnchor(e.currentTarget)}
        data-testid="filter-btn"
        data-active={count > 0}
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        style={{ ...RAIL, ...(engaged ? ENGAGED : {}) }}>
        <FilterListIcon /> Filter{count ? ` · ${count}` : ''}
      </SecondaryButton>
      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}>
        <Panel data-testid="filter-panel">
          <div>
            <FieldLabel>Name</FieldLabel>
            <div style={{ marginTop: 7 }}>
              <SearchInput
                value={typed}
                onChange={e => setTyped(e.target.value)}
                placeholder="Search by name…"
                aria-label="Search by name"
                data-testid="filter-name"
              />
            </div>
          </div>
          <ChipGroup label="File type" values={kindsIn(files)} on={filter.kinds} testId="filter-kind" onToggle={toggle('kinds')} />
          <ChipGroup
            label="Status"
            values={FILTER_STATUSES}
            on={filter.statuses}
            labelOf={s => STATUS_LABEL[s]}
            testId="filter-status"
            onToggle={toggle('statuses')}
          />
          <Result>
            <div>
              <FieldLabel>Result</FieldLabel>
              <MutedText style={{ marginTop: 4 }} data-testid="filter-result">
                Showing {shownCount} of {plural(files.length, 'file')}
              </MutedText>
            </div>
            <LinkButton type="button" onClick={() => onChange(EMPTY_FILTER)} data-testid="filter-clear">
              Clear all
            </LinkButton>
          </Result>
        </Panel>
      </Popover>
    </>
  )
}
